import { safeString, Stopwatch, Tracker } from "../../helpers";
import { getFunctionHandler, telemetry } from "../../constants";
import { getBetTrackedData } from "./getBetTrackedData";
export function parseCashOut({ retriever, dictionary, formatAmount, information, operation, isLoggedInCanBet, openRecheckLocationModal, betStatus, }) {
    if (betStatus !== "Open") {
        return undefined;
    }
    if (typeof operation !== "undefined" && operation.status !== "Declined") {
        return mapCashOutOperation[operation.status]({
            retriever,
            dictionary,
            formatAmount,
            operation,
        });
    }
    if (typeof information !== "undefined") {
        return mapCashOutInformation[information.cashOutStatus]({
            retriever,
            dictionary,
            formatAmount,
            information,
            isLoggedInCanBet,
            openRecheckLocationModal,
        });
    }
    return undefined;
}
const mapCashOutOperation = new Proxy({
    Accepted: parseCashOutOperationAcceptedResult,
    NewOffer: parseCashOutOperationNewOfferResult,
    LiveDelay: parseCashOutDelayResult,
    TraderDelay: parseCashOutDelayResult,
}, getFunctionHandler);
function parseCashOutOperationAcceptedResult({ formatAmount, operation, }) {
    const betId = operation.cashOutOperation.cashOutInformation.betId;
    const purchaseId = operation.cashOutOperation.cashOutInformation.purchaseId;
    return {
        betId,
        purchaseId,
        status: "Accepted",
        formattedCashOutAmount: formatAmount(operation.cashOutOperation.cashOutAmount, ""),
    };
}
function parseCashOutOperationNewOfferResult({ retriever, dictionary, formatAmount, operation, }) {
    const betId = operation.cashOutOperation.cashOutInformation.betId;
    const purchaseId = operation.cashOutOperation.cashOutInformation.purchaseId;
    return {
        betId,
        purchaseId,
        status: operation.status,
        message: dictionary.myBetsDictionary.CashOutNewOfferMessage,
        formattedCashOutAmount: formatAmount(operation.cashOutOperation.cashOutAmount, dictionary.myBetsDictionary.CashOutConfirm),
        onClick: () => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const trackedData = getBetTrackedData(retriever, betId);
            Tracker.getInstance()
                .saveEventData(betId, trackedData)
                .trackEvent(telemetry.events.customTrace, {
                ClientType: telemetry.clientType,
                Trace: telemetry.trace.newOfferConfirm,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.newOfferFlow}:${betId}`),
                Status: operation.status,
                StatusUpdateTime: safeString(operation.statusUpdateTime),
                MyBetsVersion: telemetry.version,
            }, betId)
                .trackEvent(telemetry.events.cashOut, {
                ClientType: telemetry.clientType,
                Action: telemetry.actions.cashOut.acceptNewOffer,
                Page: telemetry.page,
                MyBetsVersion: telemetry.version,
                Label: operation.cashOutOperation.cashOutInformation.betId,
                Component: "CashOutViewConfirmChanges",
            }, betId);
            retriever.acceptCashOutNewOffer({
                betId,
                newOfferId: safeString(operation.newOfferId),
            });
        },
    };
}
function parseCashOutDelayResult({ formatAmount, operation, }) {
    const betId = operation.cashOutOperation.cashOutInformation.betId;
    const purchaseId = operation.cashOutOperation.cashOutInformation.purchaseId;
    return {
        betId,
        purchaseId,
        status: operation.status,
        formattedCashOutAmount: formatAmount(operation.cashOutOperation.cashOutAmount, ""),
    };
}
const mapCashOutInformation = new Proxy({
    Enabled: parseCashOutInformationRegular,
    Suspended: parseCashOutInformationSuspended,
    Invalid: parseCashOutInformationInvalid,
    Error: parseCashOutInformationError,
    Processing: parseCashOutInformationProcessing,
    Confirm: parseCashOutInformationConfirm,
    // statuses Disabled and Closed
    // are not mentioned in this map
    // and not handled on purpose
    // both of those are statuses
    // where we do not want to show the cashout
    // they are handled by the proxy's get handler
    // see more on proxies:
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy/Proxy/get
    // see more about this statuses:
    // https://github.com/WeKnowSports/CashoutAPI/wiki/Subscription-to-Information
}, getFunctionHandler);
function parseCashOutInformationError({ retriever, dictionary, formatAmount, information, }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Error",
        message: dictionary.myBetsDictionary.CashOutGenericErrorMessage,
        formattedCashOutAmount: formatAmount(information.fullReturn, dictionary.myBetsDictionary.CashOut),
        onClick: () => {
            retriever.updateCashOutInformationStatus(information.betId, "Confirm");
        },
    };
}
function parseCashOutInformationInvalid({ retriever, dictionary, formatAmount, information }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Invalid",
        message: dictionary.myBetsDictionary.CashOutOddsChangedMessage,
        formattedCashOutAmount: formatAmount(information.fullReturn, dictionary.myBetsDictionary.CashOut),
        onClick: () => {
            retriever.updateCashOutInformationStatus(information.betId, "Confirm");
        },
    };
}
function parseCashOutInformationProcessing({ formatAmount, information }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Processing",
        formattedCashOutAmount: formatAmount(information.fullReturn, ""),
    };
}
function parseCashOutInformationRegular({ retriever, dictionary, formatAmount, information, isLoggedInCanBet, openRecheckLocationModal, }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Enabled",
        formattedCashOutAmount: formatAmount(information.fullReturn, dictionary.myBetsDictionary.CashOut),
        onClick: () => {
            const trackedData = getBetTrackedData(retriever, information.betId);
            if (isLoggedInCanBet()) {
                Stopwatch.start(`${telemetry.trace.cashOutFlow}:${information.betId}`);
                Tracker.getInstance()
                    .saveEventData(information.betId, trackedData)
                    .trackEvent(telemetry.events.cashOut, {
                    ClientType: telemetry.clientType,
                    Action: telemetry.actions.cashOut.initiateCashOut,
                    Page: telemetry.page,
                    MyBetsVersion: telemetry.version,
                    Label: information.betId,
                    Component: "CashOutViewRegular",
                }, information.betId);
                retriever.updateCashOutInformationStatus(information.betId, "Confirm");
            }
            else {
                Tracker.getInstance().trackEvent(telemetry.events.cashOut, Object.assign(Object.assign({}, trackedData), { ClientType: telemetry.clientType, Action: telemetry.actions.cashOut.openRecheckLocationModal, Page: telemetry.page, MyBetsVersion: telemetry.version, Label: information.betId, Component: "CashOutViewRegular" }));
                openRecheckLocationModal();
            }
        }
    };
}
function parseCashOutInformationConfirm({ retriever, dictionary, formatAmount, information, }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Confirm",
        formattedCashOutAmount: formatAmount(information.fullReturn, dictionary.myBetsDictionary.CashOutConfirm),
        onClick: () => {
            const trackedData = getBetTrackedData(retriever, information.betId);
            Tracker.getInstance()
                .saveEventData(information.betId, trackedData)
                .trackEvent(telemetry.events.customTrace, {
                ClientType: telemetry.clientType,
                Action: telemetry.trace.userConfirm,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.cashOutFlow}:${information.betId}`),
                MyBetsVersion: telemetry.version,
            }, information.betId)
                .trackEvent(telemetry.events.cashOut, {
                ClientType: telemetry.clientType,
                Action: telemetry.actions.cashOut.confirmCashOut,
                Page: telemetry.page,
                Label: information.betId,
                MyBetsVersion: telemetry.version,
                Component: "CashOutViewConfirm",
            }, information.betId);
            retriever.updateCashOutInformationStatus(information.betId, "Processing");
            retriever.requestCashOut({
                cashOutInformation: information,
                cashOutAmount: information.fullReturn
            });
        }
    };
}
function parseCashOutInformationSuspended({ dictionary, information, }) {
    return {
        betId: information.betId,
        purchaseId: information.purchaseId,
        status: "Suspended",
        formattedCashOutAmount: dictionary.myBetsDictionary.CashOutSuspended,
    };
}
//# sourceMappingURL=parseCashOut.js.map